/**
 * Calendar-day arithmetic.
 *
 * Every date in the finance engine represents a CALENDAR DAY, not an instant.
 * The canonical in-memory representation is a Date at LOCAL midnight.
 *
 * Two rules, both of which fix live bugs:
 *
 *  1. Never `new Date("YYYY-MM-DD")`. That parses as UTC midnight, which is the
 *     previous day in any western timezone — this is why a one-time payment
 *     entered for Aug 6 rendered on Aug 5. Use `parseLocalDate` instead.
 *
 *  2. Never `setMonth()` on a Date carrying a day-of-month > 28. Jan 31 + 1
 *     month overflows to Mar 3. Use `addMonths` / `payDayFor`, which clamp.
 *
 * Serialization uses full ISO strings (which carry the UTC offset), so a
 * local-midnight Date round-trips through Prisma back to local midnight.
 */

/** Non-mutating start-of-day. The old engine called `d.setHours(0,0,0,0)` on
 *  caller-owned Dates, silently mutating shared objects. */
export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function today(): Date {
  return startOfDay(new Date());
}

/** 'YYYY-MM-DD' in LOCAL time. The only correct way to compare two calendar
 *  days — `getTime()` comparison breaks the moment a value round-trips a TZ. */
export function toDayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Parse 'YYYY-MM-DD' (e.g. from `<input type="date">`) as LOCAL midnight. */
export function parseLocalDate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

/** Coerce anything date-ish (Date | ISO string | day key) to local midnight. */
export function toLocalDay(value: Date | string): Date {
  if (value instanceof Date) return startOfDay(value);
  // Bare 'YYYY-MM-DD' would parse as UTC — route it through parseLocalDate.
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return parseLocalDate(value);
  return startOfDay(new Date(value));
}

export function isSameDay(a: Date, b: Date): boolean {
  return toDayKey(a) === toDayKey(b);
}

export function daysInMonth(year: number, monthIndex: number): number {
  return new Date(year, monthIndex + 1, 0).getDate();
}

export function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

/** Add months, clamping the day to the target month's length.
 *  Jan 31 + 1 month => Feb 28/29 (not Mar 3). */
export function addMonths(d: Date, n: number): Date {
  const y = d.getFullYear();
  const m = d.getMonth() + n;
  const targetYear = y + Math.floor(m / 12);
  const targetMonth = ((m % 12) + 12) % 12;
  const day = Math.min(d.getDate(), daysInMonth(targetYear, targetMonth));
  return new Date(targetYear, targetMonth, day);
}

/** The payment date for a given statement month, clamped to month length.
 *  A card due on the 31st pays Feb 28 (or 29 in a leap year). */
export function payDayFor(year: number, monthIndex: number, dueDay: number): Date {
  const y = year + Math.floor(monthIndex / 12);
  const m = ((monthIndex % 12) + 12) % 12;
  return new Date(y, m, Math.min(Math.max(1, dueDay), daysInMonth(y, m)));
}

/** Whole calendar days from `a` to `b` (b - a). Negative if b precedes a. */
export function daysBetween(a: Date, b: Date): number {
  const MS = 24 * 60 * 60 * 1000;
  // Use UTC of the local Y/M/D so DST transitions can't produce 23/25h days.
  const ua = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const ub = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((ub - ua) / MS);
}

/** Count of `dueDay` occurrences strictly after `from`, up to and including `to`. */
export function dueDayOccurrencesBetween(from: Date, to: Date, dueDay: number): number {
  if (to <= from) return 0;
  let count = 0;
  let cursor = payDayFor(from.getFullYear(), from.getMonth(), dueDay);
  if (cursor <= from) cursor = addMonths(cursor, 1);
  while (cursor <= to) {
    count += 1;
    cursor = payDayFor(cursor.getFullYear(), cursor.getMonth() + 1, dueDay);
  }
  return count;
}

/** For `<input type="date">` values. */
export function toDateInputValue(d: Date): string {
  return toDayKey(d);
}
